import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Clock, 
  Heart,
  XCircle,
  CreditCard,
  CheckCircle2,
  Truck,
  ShoppingBag,
  Mail,
  Phone,
  Globe,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import GastrofestForm from '../components/GastrofestForm';
import { 
  festivalInfo, 
  programSchedule, 
  whyJoinReasons, 
  vendorCategories, 
  importantRules, 
  pricingConditions,
  programHighlights,
  contactInfo,
  flyerImage
} from '../data/gastrofestData';

const GastrofestLanding = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const scrollToForm = () => {
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const iconComponents = {
    Users, Clock, Calendar, CheckCircle2, Heart, XCircle, CreditCard, Globe, MapPin
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black z-50 border-b border-green-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-300 hover:text-green-500"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Přehled akcí</span>
            </Button>
            <h2 className="text-2xl font-bold">
              <span className="text-white">GASTRO</span>
              <span className="text-yellow-400">FESTIVAL</span>
            </h2>
          </div>
          <Button 
            onClick={scrollToForm}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-2 rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            PŘIHLÁSIT SE
          </Button>
        </div>
      </header>

      {/* Hero Section - Based on Design with Background Photo */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-white overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_kontakt-gastro-hub/artifacts/3plv5bqw_fotka%20uvodni.jpg"
            alt="Gastrofestival - Kuchař v akci"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/75"></div>
          {/* Green Accent Bars (left and right) */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-green-500 to-transparent opacity-20"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-green-500 to-transparent opacity-20"></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          {/* Small top text */}
          <div className="mb-4">
            <p className="text-white text-lg tracking-widest">CESTA KOLEM SVĚTA</p>
          </div>
          
          {/* Main Title - matching design */}
          <div className="mb-8">
            <h1 className="text-7xl md:text-8xl font-bold mb-4 leading-tight">
              <span className="text-white">GASTRO</span><br/>
              <span className="text-yellow-400">FESTIVAL</span>
            </h1>
          </div>

          {/* Purple Badge */}
          <div className="inline-block bg-purple-600 text-white px-8 py-3 rounded-full font-bold text-xl mb-8">
            VSTUP ZDARMA
          </div>
          
          {/* Date */}
          <div className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-yellow-400">16.-17.5.</span>
            <span className="text-white">2026</span>
          </div>

          {/* Location */}
          <div className="text-3xl md:text-4xl mb-4 text-white font-bold">
            UHERSKÉ HRADIŠTĚ
          </div>
          <div className="text-xl md:text-2xl mb-12 text-yellow-400">
            MASARYKOVO NÁMĚSTÍ
          </div>

          {/* CTA Button */}
          <Button 
            onClick={scrollToForm}
            size="lg"
            className="bg-green-600 hover:bg-green-700 text-white text-xl px-12 py-7 font-bold rounded-full shadow-xl hover:shadow-2xl transition-all"
          >
            PŘIHLÁSIT SE NYNÍ
          </Button>

          {/* Bottom green text */}
          <div className="mt-12 text-green-500 text-xl md:text-2xl font-bold">
            OCHUTNEJTE CELOSVĚTOVOU KUCHYNI NA JEDNOM MÍSTĚ V JEDEN DEN!
          </div>

          {/* World Flags */}
          <div className="mt-8 flex justify-center items-center gap-4 flex-wrap">
            {['🇳🇴', '🇺🇸', '🇩🇪', '🇧🇷', '🇨🇦', '🇸🇰', '🇳🇱', '🇬🇧', '🇭🇷', '🇮🇹', '🇯🇵', '🇸🇪', '🇷🇴'].map((flag, i) => (
              <span key={i} className="text-4xl">{flag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Festival Stats */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-yellow-50 border-b">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-green-600">10 000+</div>
              <div className="text-gray-700 mt-2">Návštěvníků</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-yellow-500">30+</div>
              <div className="text-gray-700 mt-2">Stánků světové kuchyně</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-purple-600">0 Kč</div>
              <div className="text-gray-700 mt-2">Vstupné</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-green-600">4.</div>
              <div className="text-gray-700 mt-2">Ročník</div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gray-900">ATMOSFÉRA </span>
              <span className="text-green-600">FESTIVALU</span>
            </h2>
            <p className="text-xl text-gray-600">Podívejte se na Gastrofestival Cesta Kolem Světa</p>
          </div>
          
          <div className="flex justify-center">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{maxWidth: '500px'}}>
              <iframe 
                src="https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F3190775791065287%2F&show_text=false&width=267&t=0" 
                width="267" 
                height="476" 
                style={{border: 'none', overflow: 'hidden'}} 
                scrolling="no" 
                frameBorder="0" 
                allowFullScreen={true}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gray-900">GALERIE </span>
              <span className="text-green-600">Z FESTIVALU</span>
            </h2>
            <p className="text-xl text-gray-600">Podívejte se na atmosféru předchozích ročníků</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Photo 1 - Pita wrap */}
            <div className="relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-shadow group">
              <img 
                src="https://customer-assets.emergentagent.com/job_kontakt-gastro-hub/artifacts/9oj66h3v_424611545_949574440510503_5145737766134933970_n.jpg"
                alt="Multikulturní jídlo"
                className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>

            {/* Photo 2 - Crowd view */}
            <div className="relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-shadow group">
              <img 
                src="https://customer-assets.emergentagent.com/job_kontakt-gastro-hub/artifacts/uywm3fs3_438173232_949574613843819_4131053990424114538_n.jpg"
                alt="Soutěž v pojídání - dav"
                className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>

            {/* Photo 3 - Catering girls */}
            <div className="relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-shadow group">
              <img 
                src="https://customer-assets.emergentagent.com/job_kontakt-gastro-hub/artifacts/heyeiss0_438173465_949574093843871_549612941248441729_n.jpg"
                alt="Prodejci na festivalu"
                className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>

            {/* Photo 4 - Magic show */}
            <div className="relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-shadow group md:col-span-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_kontakt-gastro-hub/artifacts/s3y28237_438170582_949574507177163_3426896523971027866_n%281%29.jpg"
                alt="Program pro děti"
                className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>

            {/* Photo 5 - Paella chef */}
            <div className="relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-shadow group">
              <img 
                src="https://customer-assets.emergentagent.com/job_kontakt-gastro-hub/artifacts/v167zgiu_441155966_949574237177190_3729141305939940302_n.jpg"
                alt="Příprava paelly"
                className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Schedule */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">
            <span className="text-gray-900">Program </span>
            <span className="text-green-600">festivalu</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-green-500">
              <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                <CardTitle className="text-2xl">{programSchedule.saturday.day}</CardTitle>
                <CardDescription className="text-green-100 text-lg">{programSchedule.saturday.time}</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 text-lg">{programSchedule.saturday.description}</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-yellow-500">
              <CardHeader className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
                <CardTitle className="text-2xl">{programSchedule.sunday.day}</CardTitle>
                <CardDescription className="text-yellow-100 text-lg">{programSchedule.sunday.time}</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 text-lg">{programSchedule.sunday.description}</p>
              </CardContent>
            </Card>
          </div>

          {/* Program Highlights */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Program akce:</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {programHighlights.map((highlight, index) => (
                <div key={index} className="bg-green-50 border-l-4 border-green-500 p-4">
                  <p className="text-gray-900 font-semibold">✓ {highlight}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Join */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">
            <span className="text-gray-900">Proč se </span>
            <span className="text-green-600">přihlásit?</span>
          </h2>
          <p className="text-center text-xl text-gray-600 mb-12">
            Gastrofestival nabízí jedinečnou příležitost prezentovat vaše kulinářské speciality tisícům návštěvníků
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyJoinReasons.map((reason, index) => {
              const IconComponent = iconComponents[reason.icon];
              return (
                <Card key={index} className="border-2 border-green-200 hover:border-green-500 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-green-600 rounded-full flex items-center justify-center mb-4">
                      {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{reason.title}</h3>
                    <p className="text-gray-600">{reason.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vendor Categories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">
            <span className="text-gray-900">Koho </span>
            <span className="text-green-600">hledáme?</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <Card className="border-2 border-green-500">
              <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                <div className="flex items-center gap-3">
                  <Truck className="w-8 h-8" />
                  <CardTitle className="text-2xl">{vendorCategories.priority.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 mb-4">{vendorCategories.priority.description}</p>
                <ul className="space-y-2">
                  {vendorCategories.priority.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-yellow-500">
              <CardHeader className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
                <div className="flex items-center gap-3">
                  <ShoppingBag className="w-8 h-8" />
                  <CardTitle className="text-2xl">{vendorCategories.additional.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 mb-4">{vendorCategories.additional.description}</p>
                <ul className="space-y-2">
                  {vendorCategories.additional.categories.map((cat, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{cat}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">
            <span className="text-gray-900">Ceník a </span>
            <span className="text-green-600">podmínky</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="border-2 border-green-500 bg-white">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Truck className="w-10 h-10 text-green-600" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{pricingConditions.foodTrucks.title}</h3>
                    <p className="text-gray-600">{pricingConditions.foodTrucks.size}</p>
                  </div>
                </div>
                <div className="text-4xl font-bold text-green-600 mb-2">{pricingConditions.foodTrucks.price}</div>
                <p className="text-gray-600">{pricingConditions.foodTrucks.category}</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-yellow-500 bg-white">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <ShoppingBag className="w-10 h-10 text-yellow-600" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{pricingConditions.additional.title}</h3>
                    <p className="text-gray-600">{pricingConditions.additional.size}</p>
                  </div>
                </div>
                <div className="text-4xl font-bold text-yellow-600 mb-2">{pricingConditions.additional.price}</div>
                <p className="text-gray-600">{pricingConditions.additional.category}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-1 gap-6">
            <Card className="bg-white border-2 border-purple-300">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{pricingConditions.electricity.title}</h3>
                <div className="space-y-3">
                  {pricingConditions.electricity.options.map((opt, index) => (
                    <div key={index} className="flex justify-between items-center border-b border-gray-200 pb-2">
                      <span className="text-gray-700 font-semibold">{opt.range}</span>
                      <span className="text-purple-600 font-bold">{opt.price}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-3">{pricingConditions.electricity.note}</p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 p-4 bg-green-50 border-l-4 border-green-500">
            <p className="text-gray-700">
              <strong>✓ Bez vratné kauce:</strong> Na Gastrofestivalu nevybíráme vratnou kauci
            </p>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-500">
            <p className="text-gray-700">
              <strong>Poznámka:</strong> {pricingConditions.note}
            </p>
          </div>
        </div>
      </section>

      {/* Important Rules */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">
            <span className="text-gray-900">Důležité </span>
            <span className="text-red-600">podmínky</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {importantRules.map((rule, index) => {
              const IconComponent = iconComponents[rule.icon];
              return (
                <Card key={index} className="border-2 border-red-300 bg-red-50">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                        {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{rule.title}</h3>
                        <p className="text-gray-700 mb-2">{rule.description}</p>
                        {rule.note && (
                          <p className="text-sm text-red-700 font-semibold">ℹ️ {rule.note}</p>
                        )}
                        {rule.warning && (
                          <p className="text-sm text-red-700 font-bold mt-2">⚠️ {rule.warning}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-8">Máte dotazy?</h2>
          <p className="text-xl mb-8">Kontaktujte nás</p>
          
          <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
            <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-3 text-xl hover:text-yellow-400 transition-colors">
              <Phone className="w-6 h-6" />
              <span>{contactInfo.phone}</span>
            </a>
            <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 text-xl hover:text-yellow-400 transition-colors">
              <Mail className="w-6 h-6" />
              <span>{contactInfo.email}</span>
            </a>
          </div>
          <p className="mt-6 text-lg">{contactInfo.name}</p>
        </div>
      </section>

      {/* Application Form */}
      {showForm && (
        <section id="application-form" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">
                <span className="text-gray-900">Registrační </span>
                <span className="text-green-600">formulář</span>
              </h2>
              <p className="text-xl text-gray-600">
                Vyplňte formulář a staňte se součástí Gastrofestivalu Cesta Kolem Světa 2026
              </p>
            </div>
            <GastrofestForm />
          </div>
        </section>
      )}

      {/* Final CTA */}
      {!showForm && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-green-600 to-yellow-500 text-white text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl font-bold mb-6">Připraveni se přihlásit?</h2>
            <p className="text-2xl mb-8">Neváhejte a staňte se součástí největšího gastrofestivalu v Uherském Hradišti!</p>
            <Button 
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-green-600 hover:bg-gray-100 text-2xl px-16 py-8 font-bold rounded-full shadow-2xl hover:shadow-3xl transition-all"
            >
              PŘIHLÁSIT SE NYNÍ
            </Button>
          </div>
        </section>
      )}

      {/* More Events Section */}
      <section className="py-8 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white border-y-2 sm:border-y-4 border-green-400">
        <div className="max-w-5xl mx-auto text-center">
          <div className="bg-gradient-to-br from-green-50 to-yellow-50 rounded-xl sm:rounded-2xl p-6 sm:p-12 border-2 sm:border-4 border-green-300 shadow-xl">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4 text-green-700">
              🎪 Máme více festivalů!
            </h2>
            <p className="text-base sm:text-xl text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Zajímá vás prodejní místo i na dalších akcích? Podívejte se na všechny naše festivaly v roce 2026.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-green-600 to-yellow-500 hover:from-green-700 hover:to-yellow-600 text-white text-base sm:text-xl px-6 sm:px-12 py-4 sm:py-6 font-black rounded-full shadow-2xl hover:shadow-3xl transition-all w-full sm:w-auto"
            >
              🎉 Zobrazit další akce
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">
            © 2026 Gastrofestival Cesta Kolem Světa | Uherské Hradiště
          </p>
        </div>
      </footer>
    </div>
  );
};

export default GastrofestLanding;
